import {
  DEFAULT_REPORT_PAGE_HEIGHT_PX,
  DEFAULT_REPORT_PAGE_WIDTH_PX,
  ESdChangeReason,
  EUserAccessPart,
  IReportCanvasSize,
  getPaperPageSize,
  isReportCanvasEntryStale
} from "@kuika/kuika-cl-model";
import { IPaperSize, IScreenCanvasSize } from "@kuika/kuika-cl-model/src/models/screen-canvas-sizes";
import classNames from "classnames";
import React, { useCallback, useEffect, useMemo } from "react";
import deviceListJson from "../../../../../shared/data/device-list.json";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import ArrowDownSvg from "../../../../../svgs/small-icons/arrow-down.svg";
import KuikaDropdownMenu, {
  KuikaDropdownMenuItemType
} from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSmallMediumText from "../../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaTextMedium from "../../../../ui-kit/typography/TextMedium/TextMedium";
import "./report-sizes-dropdown.scss";
import { PaperUnit, convertValue, formatUnitLabel, getMinMaxForUnit } from "../utils/unit-conversion";

const DEFAULT_PAPER_SIZES: IPaperSize[] = [
  {
    key: "custom",
    label: "Custom",
    shortLabel: "Custom",
    width: 794,
    height: 1123,
    unit: "pt",
    dimensions: {
      mm: { width: 210, height: 297 },
      inch: { width: 8.27, height: 11.69 },
      pt: { width: 595.5, height: 842.25 }
    }
  },
  {
    key: "executive",
    label: "Executive: 522 x 756 points",
    shortLabel: "Executive",
    width: 693,
    height: 1008,
    unit: "pt",
    dimensions: {
      mm: { width: 184.15, height: 266.7 },
      inch: { width: 7.25, height: 10.5 },
      pt: { width: 522, height: 756 }
    }
  },
  {
    key: "folio",
    label: "Folio: 595 x 935 points",
    shortLabel: "Folio",
    width: 794,
    height: 1247,
    unit: "pt",
    dimensions: {
      mm: { width: 210, height: 330 },
      inch: { width: 8.27, height: 13 },
      pt: { width: 595, height: 935 }
    }
  },
  {
    key: "a3",
    label: "A3: 297 x 420 millimeters",
    shortLabel: "A3",
    width: 1123,
    height: 1587,
    unit: "mm",
    dimensions: {
      mm: { width: 297, height: 420 },
      inch: { width: 11.69, height: 16.54 },
      pt: { width: 842.25, height: 1190.25 }
    }
  },
  {
    key: "a4",
    label: "A4: 210 x 297 millimeters",
    shortLabel: "A4",
    width: 794,
    height: 1123,
    unit: "mm",
    dimensions: {
      mm: { width: 210, height: 297 },
      inch: { width: 8.27, height: 11.69 },
      pt: { width: 595.5, height: 842.25 }
    }
  },
  {
    key: "a5",
    label: "A5: 148 x 210 millimeters",
    shortLabel: "A5",
    width: 559,
    height: 794,
    unit: "mm",
    dimensions: {
      mm: { width: 148, height: 210 },
      inch: { width: 5.83, height: 8.27 },
      pt: { width: 419.25, height: 595.5 }
    }
  },
  {
    key: "a6",
    label: "A6: 105 x 148 millimeters",
    shortLabel: "A6",
    width: 397,
    height: 559,
    unit: "mm",
    dimensions: {
      mm: { width: 105, height: 148 },
      inch: { width: 4.13, height: 5.83 },
      pt: { width: 297.75, height: 419.25 }
    }
  },
  {
    key: "b4",
    label: "B4: 250 x 353 millimeters",
    shortLabel: "B4",
    width: 945,
    height: 1334,
    unit: "mm",
    dimensions: {
      mm: { width: 250, height: 353 },
      inch: { width: 9.84, height: 13.9 },
      pt: { width: 708.75, height: 1000.5 }
    }
  },
  {
    key: "b5",
    label: "B5: 176 x 250 millimeters",
    shortLabel: "B5",
    width: 665,
    height: 945,
    unit: "mm",
    dimensions: {
      mm: { width: 176, height: 250 },
      inch: { width: 6.93, height: 9.84 },
      pt: { width: 498.75, height: 708.75 }
    }
  },
  {
    key: "letter",
    label: "Letter: 8.5 x 11 inches",
    shortLabel: "Letter",
    width: 816,
    height: 1056,
    unit: "inch",
    dimensions: {
      mm: { width: 215.9, height: 279.4 },
      inch: { width: 8.5, height: 11 },
      pt: { width: 612, height: 792 }
    }
  },
  {
    key: "legal",
    label: "Legal: 8.5 x 14 inches",
    shortLabel: "Legal",
    width: 816,
    height: 1344,
    unit: "inch",
    dimensions: {
      mm: { width: 215.9, height: 355.6 },
      inch: { width: 8.5, height: 14 },
      pt: { width: 612, height: 1008 }
    }
  },
  {
    key: "tabloid",
    label: "Tabloid: 11 x 17 inches",
    shortLabel: "Tabloid",
    width: 1056,
    height: 1632,
    unit: "inch",
    dimensions: {
      mm: { width: 279.4, height: 431.8 },
      inch: { width: 11, height: 17 },
      pt: { width: 792, height: 1224 }
    }
  }
];

const MIN_DIMENSION = 100;
/** A4, the page a report falls back to — shared with every other surface that draws one. */
const DEFAULT_WIDTH = DEFAULT_REPORT_PAGE_WIDTH_PX;
const DEFAULT_HEIGHT = DEFAULT_REPORT_PAGE_HEIGHT_PX;

interface IProps extends IWithSDProps {}

interface IDimensionInputProps {
  value: number;
  onChange: (value: number) => void;
  onBlur: () => void;
  placeholder: string;
  unit?: PaperUnit;
}

const DimensionInput: React.FC<IDimensionInputProps> = ({ value, onChange, onBlur, placeholder, unit = "pt" }) => {
  const { min, max } = getMinMaxForUnit(unit);

  return (
    <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
      <KuikaInput
        type="number"
        placeholder={placeholder}
        className="report-sizes-dropdown__dimension-input"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        onBlur={onBlur}
        onPressEnter={onBlur}
        min={min}
        max={max}
        aria-label={`${placeholder} input`}
        size="sm"
      />
    </div>
  );
};

const ReportSizesDropdown: React.FC<IProps> = ({ sd }) => {
  const [selectedPaperSize, setSelectedPaperSize] = React.useState<IPaperSize | null>(null);
  const [selectedUnit, setSelectedUnit] = React.useState<PaperUnit>("pt");
  const [displayWidth, setDisplayWidth] = React.useState<number>(0);
  const [displayHeight, setDisplayHeight] = React.useState<number>(0);

  const [paperSizes, setPaperSizes] = React.useState<IPaperSize[]>(DEFAULT_PAPER_SIZES);
  const [screenSizeLabel, setScreenSizeLabel] = React.useState<string>("Paper Size");

  const selectedScreenCanvasSize = sd.project?.screenCanvasSizes?.screens?.find((s) => s.id === sd.sdId);

  const onSelectReportSize = useCallback(
    (paper: IPaperSize) => {
      const { project, updateProject } = sd;
      let { screenCanvasSizes } = project;
      if (!screenCanvasSizes) {
        screenCanvasSizes = { screens: [] } as any;
      }
      const screen = screenCanvasSizes?.screens?.find((s) => s.id === sd.sdId);
      let width = paper.width;
      let height = paper.height;

      const deviceList: any[] = deviceListJson as unknown as any[];
      const devices = deviceList;
      if (devices.length > 0 && sd.setSpecificDevice) {
        sd.setSpecificDevice(devices[0], true);
      }

      const canvasSizeByScreen: IScreenCanvasSize = {
        id: sd.sdId,
        width,
        height,
        deviceType: devices[0].type,
        device: devices[0],
        reportPaper: paper
      };

      if (!screenCanvasSizes.screens) screenCanvasSizes.screens = [];
      if (screen) {
        screen.width = width;
        screen.height = height;
        screen.reportPaper = paper;
      } else {
        screenCanvasSizes.screens.push(canvasSizeByScreen);
      }
      // ChangeProjectStyle is what makes processUpdateProject push a renderFrame, and the canvas
      // agent holds its own copy of the project: without it the agent kept sizing the page from
      // the PREVIOUS paper the next time anything made it re-apply its design-time CSS (toggling
      // the grid was enough).
      updateProject({ ...project, screenCanvasSizes }, true, ESdChangeReason.ChangeProjectStyle);
    },
    [sd]
  );

  /**
   * Resizes the page in the canvas straight away, before the save round-trip that eventually
   * hands the agent the new paper. Sizes come from the shared derivation so this fast path and
   * the agent cannot disagree about what the page is.
   */
  const updateIframeSize = useCallback((paper: IPaperSize) => {
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const rootDI = iframe?.contentDocument?.getElementById("rootDI");
    const pageSize: IReportCanvasSize = getPaperPageSize(paper);

    if (rootDI) {
      rootDI.style.height = `${pageSize.height}px`;
      rootDI.style.width = `${pageSize.width}px`;

      const rootDIBorderLeftWidth = parseInt(rootDI.style.borderLeftWidth) || 0;
      const rootDIBorderRightWidth = parseInt(rootDI.style.borderRightWidth) || 0;
      const rootDIBorderTopWidth = parseInt(rootDI.style.borderTopWidth) || 0;
      const rootDIBorderBottomWidth = parseInt(rootDI.style.borderBottomWidth) || 0;

      if (rootDIBorderLeftWidth > 0 || rootDIBorderRightWidth > 0) {
        rootDI.style.width = `${pageSize.width - rootDIBorderLeftWidth - rootDIBorderRightWidth + 4}px`;
      }
      if (rootDIBorderTopWidth > 0 || rootDIBorderBottomWidth > 0) {
        rootDI.style.height = `${pageSize.height - rootDIBorderTopWidth - rootDIBorderBottomWidth}px`;
        rootDI.style.minHeight = `calc(100vh - ${rootDIBorderTopWidth + rootDIBorderBottomWidth + 4}px)`;
      }
    }

    if (iframe && pageSize.height < 570) {
      iframe.style.height = `${pageSize.height}px`;
    }
  }, []);

  const updateScreenSizeLabel = useCallback((paper: IPaperSize) => {
    if (paper.key === "custom") {
      setScreenSizeLabel(`${paper.width} x ${paper.height}`);
    } else {
      const paperUnit = paper.unit || "mm";
      const unitDimensions = paper.dimensions?.[paperUnit];
      if (unitDimensions) {
        setScreenSizeLabel(
          `${paper.shortLabel} ${unitDimensions.width} x ${unitDimensions.height} ${formatUnitLabel(paperUnit)}`
        );
      } else {
        setScreenSizeLabel(`${paper.shortLabel} ${paper.width} x ${paper.height}`);
      }
    }
  }, []);

  const handleSelectReportSize = useCallback(
    (paper: IPaperSize, clickEvent?: boolean) => {
      if (paper.key === selectedPaperSize?.key && clickEvent) return;

      if (paper.key === "custom" && selectedPaperSize?.key === "custom" && clickEvent) {
        paper = { ...paper, width: selectedPaperSize.width, height: selectedPaperSize.height };
      }

      onSelectReportSize(paper);
      setSelectedPaperSize(paper);

      if (paper.key === "custom") {
        const width = paper?.width ?? DEFAULT_WIDTH;
        const height = paper?.height ?? DEFAULT_HEIGHT;

        const newUnit =
          selectedPaperSize?.key !== "custom" && selectedPaperSize?.unit ? selectedPaperSize.unit : paper.unit || "mm";

        const dimensions = paper.dimensions || {
          px: { width, height },
          mm: { width: convertValue(width, "px", "mm"), height: convertValue(height, "px", "mm") },
          inch: { width: convertValue(width, "px", "inch"), height: convertValue(height, "px", "inch") },
          pt: { width: convertValue(width, "px", "pt"), height: convertValue(height, "px", "pt") }
        };

        setSelectedUnit(newUnit);
        setDisplayWidth(dimensions[newUnit]?.width || convertValue(width, "px", newUnit));
        setDisplayHeight(dimensions[newUnit]?.height || convertValue(height, "px", newUnit));

        setPaperSizes((prevSizes) =>
          prevSizes.map((_paper) =>
            _paper.key === "custom" ? { ..._paper, width, height, dimensions, unit: newUnit } : _paper
          )
        );
      }

      updateIframeSize(paper);
      updateScreenSizeLabel(paper);
    },
    [selectedPaperSize, onSelectReportSize, updateIframeSize, updateScreenSizeLabel]
  );

  const handleDimensionChange = useCallback((dimension: "width" | "height", value: number) => {
    if (dimension === "width") {
      setDisplayWidth(value);
    } else {
      setDisplayHeight(value);
    }
  }, []);

  const handleUnitChange = useCallback(
    (newUnit: PaperUnit) => {
      if (selectedPaperSize?.key === "custom") {
        // Always convert from the pixel values (source of truth) to the new unit
        const pixelWidth = selectedPaperSize.width ?? DEFAULT_WIDTH;
        const pixelHeight = selectedPaperSize.height ?? DEFAULT_HEIGHT;

        // Convert from pixels to the new unit
        const convertedWidth = convertValue(pixelWidth, "px", newUnit);
        const convertedHeight = convertValue(pixelHeight, "px", newUnit);

        setDisplayWidth(convertedWidth);
        setDisplayHeight(convertedHeight);

        // Update dimensions with the new unit's converted values
        const updatedDimensions = {
          ...selectedPaperSize.dimensions,
          [newUnit]: {
            width: convertedWidth,
            height: convertedHeight
          }
        };

        // Create updated paper with new unit and updated dimensions
        const updatedPaper = {
          ...selectedPaperSize,
          unit: newUnit,
          dimensions: updatedDimensions
        };

        setPaperSizes((prevSizes) => prevSizes.map((paper) => (paper.key === "custom" ? updatedPaper : paper)));
        handleSelectReportSize(updatedPaper);
        setSelectedUnit(newUnit);
      } else {
        setSelectedUnit(newUnit);
      }
    },
    [selectedPaperSize, handleSelectReportSize]
  );

  const handleDimensionBlur = useCallback(
    (dimension: "width" | "height") => {
      if (selectedPaperSize?.key === "custom") {
        const currentDisplayValue = dimension === "width" ? displayWidth : displayHeight;
        // An emptied input parses to NaN, and Math.max(MIN, NaN) is NaN — which used to reach the
        // record and the page's style as "NaNpx". Leave the size alone until a number is typed.
        if (!Number.isFinite(currentDisplayValue)) {
          return;
        }

        // Convert display value to pixels for storage and round it for CSS
        const pixelValue = convertValue(currentDisplayValue, selectedUnit, "px");
        const validatedPixelValue = Math.round(Math.max(MIN_DIMENSION, pixelValue));

        // Create updated dimensions object
        // Store exact user input in the selected unit, and rounded pixel value
        // Other units will be calculated when needed from the source unit
        const updatedDimensions = {
          ...selectedPaperSize.dimensions,
          px: {
            ...selectedPaperSize.dimensions?.px,
            [dimension]: validatedPixelValue
          },
          [selectedUnit]: {
            ...selectedPaperSize.dimensions?.[selectedUnit],
            [dimension]: currentDisplayValue // Exact value entered by user
          }
        };

        // Create updated paper with the changed dimension
        const updatedPaper = {
          ...selectedPaperSize,
          [dimension]: validatedPixelValue,
          dimensions: updatedDimensions,
          unit: selectedUnit
        };

        setPaperSizes((prevSizes) => prevSizes.map((paper) => (paper.key === "custom" ? updatedPaper : paper)));

        handleSelectReportSize(updatedPaper);
      }
    },
    [selectedPaperSize, selectedUnit, displayWidth, displayHeight, handleSelectReportSize]
  );

  // useEffect(() => {
  //   const monitor = setInterval(() => {
  //     const elem = document.activeElement;
  //     if (elem?.tagName === "IFRAME" && isMenuOpen) {
  //       clearInterval(monitor);
  //       setIsMenuOpen(false);
  //     }
  //   }, 100);

  //   return () => clearInterval(monitor);
  // }, [isMenuOpen]);

  useEffect(() => {
    if (!selectedScreenCanvasSize) {
      const a4 = DEFAULT_PAPER_SIZES.find((p) => p.key === "a4");
      if (a4) {
        const a4WithMm = { ...a4, unit: "mm" as PaperUnit };
        setSelectedUnit("mm");
        setPaperSizes(DEFAULT_PAPER_SIZES);
        handleSelectReportSize(a4WithMm);
        return;
      }
      setSelectedPaperSize(null);
      setScreenSizeLabel("Paper Size");
      setPaperSizes(DEFAULT_PAPER_SIZES);
      setDisplayWidth(0);
      setDisplayHeight(0);
      setSelectedUnit("pt");
      return;
    }

    const selectedPaper = DEFAULT_PAPER_SIZES.find((paper) => paper.key === selectedScreenCanvasSize.reportPaper?.key);
    if (!selectedPaper) {
      const a4 = DEFAULT_PAPER_SIZES.find((p) => p.key === "a4");
      if (a4) {
        const a4WithMm = { ...a4, unit: "mm" as PaperUnit };
        setSelectedUnit("mm");
        setPaperSizes(DEFAULT_PAPER_SIZES);
        handleSelectReportSize(a4WithMm);
        return;
      }
      setPaperSizes(DEFAULT_PAPER_SIZES);
      return;
    }

    const reportPaper = selectedScreenCanvasSize.reportPaper;

    // Repair a record whose own width/height no longer match its paper. Something other than
    // this dropdown wrote them — the device menu did, on any screen that was open when the tool
    // switched — and the code generators still read that pair when a report has no paper, so an
    // entry left half-and-half would print at the wrong size. Writing the paper back settles
    // both halves; once settled this is a no-op, so it cannot loop.
    //
    // Only for someone who can actually save it: the save is silently dropped for a read-only
    // viewer (project-provider), which would leave them mutating the project in memory and
    // re-attempting on every open, for a record they cannot fix.
    const canRepair: boolean = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.EditProject,
      sd.project?.projectAccess
    );
    if (canRepair && isReportCanvasEntryStale(selectedScreenCanvasSize) && reportPaper) {
      handleSelectReportSize(reportPaper);
      return;
    }

    const updatedPaper =
      selectedPaper.key === "custom"
        ? {
            ...selectedPaper,
            width: reportPaper?.width ?? selectedScreenCanvasSize?.width ?? DEFAULT_WIDTH,
            height: reportPaper?.height ?? selectedScreenCanvasSize?.height ?? DEFAULT_HEIGHT,
            dimensions: reportPaper?.dimensions || selectedPaper.dimensions,
            unit: reportPaper?.unit || selectedPaper.unit
          }
        : selectedPaper;

    setSelectedPaperSize(updatedPaper);
    updateScreenSizeLabel(updatedPaper);

    if (selectedPaper.key === "custom") {
      // Get the unit from stored paper or default to pt
      const storedUnit = (reportPaper?.unit as PaperUnit) || "pt";
      setSelectedUnit(storedUnit);

      // Use stored dimensions if available
      const storedDimensions = updatedPaper.dimensions?.[storedUnit];
      if (storedDimensions) {
        setDisplayWidth(storedDimensions.width ?? 0);
        setDisplayHeight(storedDimensions.height ?? 0);
      } else {
        // Convert from pixels to the stored unit
        setDisplayWidth(convertValue(updatedPaper.width ?? DEFAULT_WIDTH, "px", storedUnit));
        setDisplayHeight(convertValue(updatedPaper.height ?? DEFAULT_HEIGHT, "px", storedUnit));
      }
      setPaperSizes((prevSizes) => prevSizes.map((paper) => (paper.key === "custom" ? updatedPaper : paper)));
    } else {
      setPaperSizes(DEFAULT_PAPER_SIZES);
      setDisplayWidth(0);
      setDisplayHeight(0);
      setSelectedUnit("pt");
    }
  }, [selectedScreenCanvasSize, updateScreenSizeLabel]);

  const menuOptions = useMemo((): KuikaDropdownMenuItemType[] => {
    return paperSizes.map((paper) => ({
      key: paper.key,
      value: paper.key,
      dataTestId: `reportPaperSizeOption-${paper.key}`,
      label: (
        <div className="report-sizes-dropdown__menu-item">
          <KuikaTextMedium>{paper.label}</KuikaTextMedium>
        </div>
      ),
      onClick: () => {
        handleSelectReportSize(paper, true);
      }
    }));
  }, [paperSizes, handleSelectReportSize]);

  return (
    <div className="report-sizes-dropdown__container">
      <KuikaDropdownMenu
        options={menuOptions}
        selected={selectedPaperSize?.key || null}
        overlayClassName="report-sizes-dropdown"
        trigger="click"
        placement="bottomLeft"
        // Portal to the body: inside row-2 the menu inherits the strip's stacking context
        // and ends up painted under the report canvas.
        getPopupContainer={() => document.body}
      >
        {(open) => (
          <div
            className={classNames("report-sizes-dropdown__trigger", {
              "report-sizes-dropdown__trigger--open": open,
              "report-sizes-dropdown__trigger--custom": selectedPaperSize?.key === "custom"
            })}
            data-testid="reportPaperSizeDropdownButton"
            role="button"
            tabIndex={0}
            aria-label="Select paper size"
            aria-expanded={open}
          >
            {selectedPaperSize?.key === "custom" ? (
              <div className="report-sizes-dropdown__custom-inputs">
                <DimensionInput
                  value={displayWidth}
                  onChange={(value) => handleDimensionChange("width", value)}
                  onBlur={() => handleDimensionBlur("width")}
                  placeholder="Width"
                  unit={selectedUnit}
                />
                <KuikaSmallMediumText>x</KuikaSmallMediumText>
                <DimensionInput
                  value={displayHeight}
                  onChange={(value) => handleDimensionChange("height", value)}
                  onBlur={() => handleDimensionBlur("height")}
                  placeholder="Height"
                  unit={selectedUnit}
                />
                <div
                  className="report-sizes-dropdown__unit"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <KuikaDropdownMenu
                    options={[
                      { key: "mm", value: "mm", label: "mm" },
                      { key: "inch", value: "inch", label: "inch" },
                      { key: "pt", value: "pt", label: "pt" }
                    ]}
                    selected={selectedUnit}
                    trigger="click"
                    placement="bottomLeft"
                    setSelected={(unit) => handleUnitChange(unit as PaperUnit)}
                  >
                    {() => <KuikaSmallMediumText>{formatUnitLabel(selectedUnit)}</KuikaSmallMediumText>}
                  </KuikaDropdownMenu>
                </div>
              </div>
            ) : (
              <KuikaTextMedium className="report-sizes-dropdown__label">{screenSizeLabel}</KuikaTextMedium>
            )}
            <ArrowDownSvg
              className={`report-sizes-dropdown__arrow ${open ? "report-sizes-dropdown__arrow--open" : ""}`}
            />
          </div>
        )}
      </KuikaDropdownMenu>
    </div>
  );
};

const reportSizesDropdown = withSD<{}>(ReportSizesDropdown);
export { DimensionInput, reportSizesDropdown as ReportSizesDropdown };
